/**
 * Track Object Utils
 */

import { getResponsiveThumbnailAssets } from './image';

import { chain, first, get, lowerCase, some, sortBy } from 'lodash';
import * as React from 'react';

import AddToCartButton from '../components/atoms/buttons/AddToCartButton';
import { AddToCartButtonOwnProps } from '../components/atoms/buttons/AddToCartButton/index';
import Button, { ButtonProps } from '../components/atoms/buttons/Button';
import TrackLicenseBuilderButton, {
  ExposedTrackLicenseBuilderButtonProps,
} from '../components/atoms/buttons/LicenseBuilderButton/TrackLicenseBuilderButton';
import { IconWarning } from '../components/atoms/images/Icon';
import PriceTag from '../components/atoms/text/PriceTag';
import { Tooltip } from '../components/atoms/text/Tooltip';
// import { MakeAnOfferTooltip } from '../components/molecules/messaging/MakeAnOfferTooltip';
import { ArtistPagePath } from '../components/pages/Artist';
import { TrackPagePath } from '../components/pages/Track';
// import { baseline } from '../components/styles/grid';
// import { CurrencyContext } from '../contexts/currency';
// import { getFormattedMoney } from './money';

interface IGetTrackAssetPayload {
  assets: GQL.IAsset[];
  version?: string;
  type?: string;
  format?: string;
}

interface IGetTrackDurationPayload {
  track: GQL.ITrackSearchResult;
  version?: GQL.IAsset;
}

const defaultList = [];

export const getTrackAssetsForFormat = (needle: string) => (assets: GQL.IAsset[]) =>
  assets.filter(({ file_format }) => lowerCase(file_format!) === lowerCase(needle));

export const getTrackAssetsForType = (needle: string) => (assets: GQL.IAsset[]) =>
  assets.filter(({ type }) => lowerCase(type!) === lowerCase(needle));

export const getTrackAssetsForVersion = (needle: string) => (assets: GQL.IAsset[]) =>
  assets.filter(({ version }) => lowerCase(version!) === lowerCase(needle));

export const AssetVersionRank = {
  original: 0,
  instrumental: 1,
  underscore: 2,
};

export function sortAssetsByVersion(assets: GQL.IAsset[]) {
  return sortBy(assets, (a) => get(AssetVersionRank, lowerCase(a.version!), 100)) as GQL.IAsset[];
}

export const getTrackAssets = ({ assets, format, type, version }: IGetTrackAssetPayload) =>
  chain(sortAssetsByVersion(assets) || defaultList)
    .thru(format ? getTrackAssetsForFormat(format) : (a) => a)
    .thru(type ? getTrackAssetsForType(type) : (a) => a)
    .thru(version ? getTrackAssetsForVersion(version) : (a) => a)
    .value();

export const getTrackAsset = (payload: IGetTrackAssetPayload) => first(getTrackAssets(payload));

export function getTrackInstrumentalAsset(assets: GQL.IAsset[]) {
  const format = 'mp3';
  return getTrackAsset({ assets, format, version: 'instrumental', type: 'track-preview' })
    || getTrackAsset({ assets, format, version: 'instrumental', type: 'track' });
}
export function getDefaultTrackInstrumentalAsset(assets: GQL.IAsset[]) {
  return getTrackInstrumentalAsset(assets) || getDefaultTrackAsset(assets);
}

export function getDefaultTrackAsset(assets: GQL.IAsset[], version = 'original') {
  const format = 'mp3';
  return getTrackAsset({ assets, format, version, type: 'track-preview' })
    || getTrackAsset({ assets, format, version, type: 'track' })
    || getTrackInstrumentalAsset(assets)
    || getTrackAsset({ assets, format, type: 'track-preview' })
    || getTrackAsset({ assets, format, type: 'track' })
    || {} as any; // To mitigate undefined dereference exceptions
}

export const getResponsiveTrackThumbnail = (track: GQL.ITrack | GQL.ITrackSearchResult) =>
  getResponsiveThumbnailAssets(track.assets || defaultList, (track.artist && track.artist.assets) || defaultList);

export const getTrackResultsFromLineItems = (lineItems: GQL.ILicenseLineItem[]) => lineItems.map(({ track }) => track!);

export function getTrackDuration({ track, version = getDefaultTrackAsset(track.assets!) }: IGetTrackDurationPayload) {
  const fallback = track.duration || 0;
  return !!version.metadata ? version.metadata.duration || fallback : fallback;
}

export function getTrackPagePath(track: GQL.ITrackSearchResult) {
  return TrackPagePath(track.slug!);
}

export function getArtistPagePath(track: GQL.ITrackSearchResult) {
  return ArtistPagePath(track.artist!.slug!);
}

interface ITrackPriceElementButtonProps {
  buttonProps?: ButtonProps;
  addToCartButtonProps?: Partial<AddToCartButtonOwnProps>;
  licenseBuilderButtonProps?: Partial<ExposedTrackLicenseBuilderButtonProps>;
}

export function getTrackPriceElement(
  track: GQL.ITrackSearchResult,
  {
    buttonProps,
    addToCartButtonProps,
    licenseBuilderButtonProps,
  }: ITrackPriceElementButtonProps,
) {
  if (track.hasOwnProperty('price') === false) {
    return undefined;
  }

  if (trackHasExclusions(track)) {
    return (
      <Tooltip
        content="This track cannot be licensed in the selected region."
        size="xsmall"
        theme="black"
        placement="bottom-end"
      >
        <Button
          variant="icon"
          textColor="bw7"
          icon={<IconWarning />}
        />
      </Tooltip>
    );
  }

  if (canLicenseTrack(track)) {
    return (
      <>
        <AddToCartButton
          item={track}
          label={<PriceTag amount={track.price!} />}
          stopPropagationOnClick
          {...buttonProps}
          {...addToCartButtonProps}
        >
          {/* <CurrencyContext.Consumer>
            {({ currency }) => (
              <MakeAnOfferTooltip
                artist={track.artist!.name!}
                trackTitle={track.title!}
                trackPrice={getFormattedMoney(track.price!, currency)}
                usages={addToCartButtonProps && addToCartButtonProps.usages}
                style={{ marginRight: !!!buttonProps || !!!buttonProps.size ? -baseline / 2 : 0 }}
              />
            )}
          </CurrencyContext.Consumer> */}
        </AddToCartButton>
      </>
    );
  }

  // Price at 0 means no license is set. We display a button that opens the license builder
  return (
    <TrackLicenseBuilderButton
      variant="pill"
      // icon={<IconAddToCart />}
      label="Show&nbsp;Price"
      colorScheme="accent"
      size="xsmall"
      track={track.slug!}
      stopPropagation
      withShadow
      {...licenseBuilderButtonProps}
    />
  );
}

export function canLicenseTrack(track: GQL.ITrackSearchResult) {
  return  !trackHasExclusions(track) && !!track.price;
}

export function trackHasExclusions(track: GQL.ITrackSearchResult) {
  return !!track.matched_exclusions && track.matched_exclusions!.length !== 0;
}

export function compareResponsiveImage(a: IResponsiveImage | undefined, b: IResponsiveImage | undefined) {
  if (a && !b || b && !a) {
    return false;
  }

  let valid = true;

  if (a && b) {
    valid = !some(a, (aImages, size) => {
      const bImages = b[size] as IImageData[] | undefined;
      if (aImages && !bImages || bImages && !aImages) {
        return true;
      }

      if (aImages && bImages) {
        return aImages.some((imageData, index) => imageData.path !== bImages[index].path);
      }

      return false;
    });
  }

  return valid;
}

export interface IPropsWithTrackResult {
  trackResult?: GQL.ITrackSearchResult;
}

export interface IPropsWithTrackId {
  trackId?: string;
}

export const getTrackFromProps = (_: IStoreState, props: IPropsWithTrackResult) => props.trackResult;
export const getTrackIdFromProps = (_: IStoreState, { trackId }: IPropsWithTrackId) => trackId;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/utils/track.tsx